<template>
  <div id="add-multiple-orders-modal">
    <v-layout row justify-center>
      <v-dialog
        v-model="addMultipleOrdersModal"
        persistent
        max-width="1600px"
        width="96vw"
      >
        <v-card>
          <v-card-title>
            <v-flex xs11 sm10 md10>
              <span class="headline">{{ $t('orders.bulkNewOrdersTitle') }}</span>
            </v-flex>
            <v-flex xs1 sm2 md2>
              <v-btn
                flat
                @click="handleClose"
                class="close"
              >
                <span aria-hidden="true">
                  <i class="material-icons" style="font-size: 24px; vertical-align: middle;">close</i>
                </span>
              </v-btn>
            </v-flex>
          </v-card-title>
          <form @submit.prevent="submitAllOrders">
          <v-card-text style="overflow: visible; padding-bottom: 12px;">
            <v-alert type="success" :value="checkAlert">
              {{ $t('orders.allItemsOrderedSuccessfully') }}
            </v-alert>
            <v-alert type="error" :value="hasError">
              {{ errorMessage }}
            </v-alert>

            <!-- Top selectors: Customer + Warehouse + Transportation -->
            <v-layout wrap>
              <v-flex
                class="px-2"
                xs12
                sm6
                md6
                v-if="loggedInUserData.is_staff"
              >
                <v-select
                  autocomplete
                  :items="customers"
                  item-text="text"
                  item-value="value"
                  v-model="selectedCustomer"
                  :filter="filterCustomers"
                  :label="$t('columns.customer') + '*'"
                  :error-messages="customerError ? [$t('orders.customerRequiredError')] : []"
                >
                </v-select>
              </v-flex>

              <v-flex class="px-2" xs12 sm6 md6>
                <v-select
                  :items="warehouses"
                  item-value="value"
                  v-model="selectedWarehouse"
                  :label="$t('orders.warehouseCountryLabel') + '*'"
                  :error-messages="warehouseError ? [$t('orders.warehouseRequiredError')] : []"
                  @input="handleSelectWarehouse"
                >
                </v-select>
              </v-flex>
            </v-layout>

            <!-- Items Table -->
            <div style="overflow-x: auto; margin-top: 16px;">
              <table
                class="table table-bordered"
                style="min-width: 1400px; margin-bottom: 0; table-layout: fixed;"
              >
                <thead>
                  <tr style="background-color: #f5f5f5;">
                    <th style="width: 3%; text-align: center; padding: 8px 4px;">#</th>
                    <th style="width: 13%; text-align: center; padding: 8px 4px;">{{ $t('columns.itemName') }}*</th>
                    <th style="width: 9%; text-align: center; padding: 8px 4px;">{{ $t('columns.brand') }}</th>
                    <th style="width: 9%; text-align: center; padding: 8px 4px;">{{ $t('columns.linkUrl') }}*</th>
                    <th style="width: 7%; text-align: center; padding: 8px 4px;">{{ $t('columns.image') }}</th>
                    <th style="width: 7%; text-align: center; padding: 8px 4px;">{{ $t('columns.preview') }}</th>
                    <th style="width: 5%; text-align: center; padding: 8px 4px;">{{ $t('columns.qty') }}*</th>
                    <th style="width: 9%; text-align: center; padding: 8px 4px;">{{ $t('customer.color') }}</th>
                    <th style="width: 8%; text-align: center; padding: 8px 4px;">{{ $t('columns.size') }}</th>
                    <th style="width: 10%; text-align: center; padding: 8px 4px;">{{ $t('columns.price') }} {{ selectedWarehouseCurrency ? '(' + selectedWarehouseCurrency + ')' : '' }}</th>
                    <th style="width: 10%; text-align: center; padding: 8px 4px;">{{ $t('columns.shipping') }} ($)</th>
                    <th style="width: 7%; text-align: center; padding: 8px 4px;">{{ $t('columns.total') }} ($)</th>
                    <th style="width: 4%; text-align: center; padding: 8px 4px;">{{ $t('columns.action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in rows"
                    :key="row.id"
                  >
                    <!-- # -->
                    <td style="text-align: center; vertical-align: middle; padding: 6px 4px; font-weight: bold;">
                      {{ index + 1 }}
                    </td>

                    <!-- Item Name -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <input
                        type="text"
                        v-model="row.product_name"
                        class="form-control"
                        :placeholder="$t('orders.itemNamePlaceholder')"
                        style="width: 100%; min-width: 0;"
                      />
                    </td>

                    <!-- Brand -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <history-autocomplete
                        v-model="row.brand"
                        :items="brandHistory"
                        variant="plain"
                        :placeholder="$t('columns.brand')"
                      />
                    </td>

                    <!-- URL -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <input
                        type="text"
                        v-model="row.product_url"
                        class="form-control"
                        placeholder="https://..."
                        style="width: 100%; min-width: 0;"
                      />
                    </td>

                    <!-- Image Upload -->
                    <td style="padding: 4px; vertical-align: middle; text-align: center;">
                      <v-btn
                        small
                        :loading="row.imageProcessing"
                        :disabled="isSubmitting"
                        @click="triggerRowFileInput(row.id)"
                        style="height: auto; min-width: unset; padding: 4px 8px; font-size: 11px;"
                      >
                        <i class="material-icons" style="font-size: 16px; vertical-align: middle;">cloud_upload</i>
                      </v-btn>
                      <input
                        type="file"
                        :ref="'fileInput_' + row.id"
                        style="display: none;"
                        accept="image/*"
                        @change="handleRowFileUpload($event, row)"
                      />
                      <div
                        v-if="row.fileName"
                        style="font-size: 10px; color: #666; margin-top: 2px; max-width: 90px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin: 2px auto 0;"
                        :title="row.fileName"
                      >
                        {{ row.fileName }}
                      </div>
                    </td>

                    <!-- Image Preview -->
                    <td style="padding: 4px; vertical-align: middle; text-align: center;">
                      <img
                        v-if="row.imagePreview"
                        :src="row.imagePreview"
                        style="width: 60px; height: 60px; object-fit: cover; border: 1px solid #ccc; border-radius: 4px; cursor: pointer;"
                        @click="openImagePreview(row.imagePreview)"
                      />
                      <div
                        v-else
                        style="width: 60px; height: 60px; background: #f5f5f5; border: 1px dashed #ccc; border-radius: 4px; display: flex; align-items: center; justify-content: center; margin: 0 auto;"
                      >
                        <i class="material-icons" style="color: #bdbdbd; font-size: 26px;">image</i>
                      </div>
                    </td>

                    <!-- Quantity -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <input
                        type="number"
                        v-model.number="row.quantity"
                        class="form-control"
                        min="1"
                        style="width: 100%; min-width: 0;"
                        @click="$event.target.select()"
                      />
                    </td>

                    <!-- Color -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <div style="display: flex; align-items: center; gap: 5px;">
                        <!-- swatch wrapper: color input overlays swatch so picker opens at correct position -->
                        <div style="position: relative; width: 26px; height: 26px; flex-shrink: 0;">
                          <div
                            :style="{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              background: row.color && row.color.trim() ? row.color : '#ffffff',
                              border: '2px solid #bdbdbd',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }"
                          ></div>
                          <input
                            type="color"
                            :value="isValidHex(row.color) ? row.color : '#ffffff'"
                            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; padding: 0; border: none;"
                            @input="handleColorPick($event, row)"
                            :title="$t('orders.pickColorTitle')"
                          />
                        </div>
                        <input
                          type="text"
                          v-model="row.color"
                          class="form-control"
                          :placeholder="$t('customer.color')"
                          style="flex: 1; min-width: 0;"
                        />
                      </div>
                    </td>

                    <!-- Size -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <history-autocomplete
                        v-model="row.size"
                        :items="sizeHistory"
                        variant="plain"
                        :placeholder="$t('columns.size')"
                      />
                    </td>

                    <!-- Price -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <input
                        type="number"
                        v-model.number="row.price"
                        class="form-control"
                        step="0.01"
                        min="0"
                        style="width: 100%; min-width: 0;"
                        @click="$event.target.select()"
                      />
                    </td>

                    <!-- Shipping Cost -->
                    <td style="padding: 4px; vertical-align: middle;">
                      <input
                        type="number"
                        v-model.number="row.shipping_cost"
                        class="form-control"
                        step="0.01"
                        min="0"
                        style="width: 100%; min-width: 0;"
                        @click="$event.target.select()"
                      />
                    </td>

                    <!-- Estimated Total -->
                    <td style="padding: 4px; vertical-align: middle; text-align: right; font-weight: bold; background-color: #f9fbe7;">
                      $ {{ rowEstimatedUsdTotal(row) }}
                    </td>

                    <!-- Delete Action -->
                    <td style="padding: 4px; text-align: center; vertical-align: middle;">
                      <v-btn
                        icon
                        small
                        @click="removeRow(row.id)"
                        :disabled="rows.length === 1"
                        style="color: #C62828; margin: 0;"
                        :title="$t('orders.removeRowTitle')"
                      >
                        <i class="material-icons">delete</i>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Add Row Button -->
            <div style="margin-top: 12px; display: flex; align-items: center; gap: 10px; flex-wrap: nowrap; overflow-x: auto; overflow-y: visible; padding-bottom: 4px;">
              <v-btn
                @click="addRows"
                class="bulk-action-btn"
                color="warning"
                outline
                style="padding: 0 16px !important; flex: 0 0 auto;"
              >
                <i class="material-icons" style="font-size: 18px; vertical-align: middle;">add</i>
                &nbsp; {{ $t('orders.addRowButton') }}
              </v-btn>
              <input
                type="number"
                v-model.number="bulkRowCount"
                min="0"
                max="100"
                :placeholder="$t('orders.countPlaceholder')"
                class="bulk-count-input"
                :title="$t('orders.numberOfRowsToAddHint')"
                style="flex: 0 0 auto;"
              />
              <v-btn
                @click="resetRows"
                class="bulk-action-btn"
                color="error"
                outline
                style="padding: 0 16px !important; flex: 0 0 auto;"
              >
                <i class="material-icons" style="font-size: 18px; vertical-align: middle;">refresh</i>
                &nbsp; {{ $t('common.reset') }}
              </v-btn>
              <span style="color: #777; font-size: 13px; flex: 0 0 auto; white-space: nowrap;">
                {{ $t('orders.itemsTotalLabel', { count: rows.length }) }}
              </span>
              <label
                v-if="$_has_perm('send_order_to_purchase')"
                class="bulk-send-to-purchase-label"
              >
                <input type="checkbox" v-model="sendToPurchase" class="bulk-send-to-purchase-checkbox" />
                {{ $t('orders.sendToPurchaseLabel') }}
              </label>
              <select
                v-if="$_has_perm('send_order_to_purchase') && sendToPurchase"
                v-model="purchaseBankId"
                class="bulk-count-input bulk-bank-select"
              >
                <option v-for="b in banks" :key="b.value" :value="b.value">{{ b.text }}</option>
              </select>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              style="height: auto;"
              @click="handleClose"
              type="button"
              outline
              color="normal"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              style="height: auto;" class="submit-btn-spacing"
              :disabled="isSubmitting"
              type="submit"
              outline
              color="success"
            >
              <i class="material-icons" style="font-size: 18px; vertical-align: middle;">send</i>
              &nbsp;
              <template v-if="uploadProgress">
                {{ $t('orders.uploadingImagesProgress', { done: uploadProgress.done, total: uploadProgress.total }) }}
              </template>
              <template v-else-if="isSubmitting">{{ $t('orders.savingOrders') }}</template>
              <template v-else>{{ $t('orders.submitAllOrdersButton') }}</template>
            </v-btn>
          </v-card-actions>
          </form>
        </v-card>
      </v-dialog>
    </v-layout>

    <!-- Full-size Image Preview Dialog -->
    <v-dialog v-model="imagePreviewDialog" max-width="600px">
      <v-card style="padding: 8px; text-align: center;">
        <img
          :src="previewDialogSrc"
          style="max-width: 100%; max-height: 80vh; object-fit: contain;"
        />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="imagePreviewDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { isAllowedImageExtension, ALLOWED_IMAGE_EXTENSIONS, compressImageFile } from '../mixins/helpers'
import HistoryAutocomplete from './HistoryAutocomplete.vue'

const MAX_ROWS = 100
// Backend storeImage() can't shrink a GIF without losing its animation, so it rejects ones over 2MB.
const MAX_GIF_BYTES = 2 * 1024 * 1024

export default {
  components: {
    HistoryAutocomplete
  },

  created() {
    this.getLoggedInUser()
    this.fetchLatestCurrencyRate()
    this.fetchBrandHistory()
    this.fetchSizeHistory()
    this.addRow()
  },

  props: [
    'addMultipleOrdersModal',
    'warehouses',
    'customers',
    'closeAddMultipleOrdersModal',
    'makeMultipleOrders',
    'loading',
    'checkAlert',
    'banks',
    'kurdistanBankId'
  ],

  data() {
    return {
      loggedInUserData: {},
      selectedCustomer: null,
      selectedWarehouse: null,
      selectedTransportationType: null,
      sendToPurchase: false,
      purchaseBankId: null,
      transportationTypes: [],
      rows: [],
      brandHistory: [],
      sizeHistory: [],
      isSubmitting: false,
      /** { done, total } while images upload in batches before the orders are saved. */
      uploadProgress: null,
      /** Rows whose selected image is still being compressed — submit waits for these. */
      pendingImageCount: 0,
      hasError: false,
      errorMessage: '',
      customerError: false,
      warehouseError: false,
      transportationError: false,
      bulkRowCount: null,
      /** Monotonic ids for v-for :key — Date.now()+random collides when bulk-adding in one tick. */
      nextRowId: 0,
      imagePreviewDialog: false,
      previewDialogSrc: '',
      latestCurrencyRate: null
    }
  },

  computed: {
    selectedWarehouseCurrency() {
      if (!this.selectedWarehouse || !this.warehouses) return null
      const warehouse = this.warehouses.find(w => w.value === this.selectedWarehouse)
      return warehouse ? warehouse.currency : null
    },

    isTurkeyWarehouse() {
      if (!this.selectedWarehouse || !this.warehouses) return false
      const warehouse = this.warehouses.find(w => w.value === this.selectedWarehouse)
      return warehouse ? (warehouse.text || '').toLowerCase().includes('turkey') : false
    },

    isUAEWarehouse() {
      if (!this.selectedWarehouse || !this.warehouses) return false
      const warehouse = this.warehouses.find(w => w.value === this.selectedWarehouse)
      const name = (warehouse ? warehouse.text || '' : '').toLowerCase()
      return name.includes('uae') || name.includes('emirat')
    },

    isChinaWarehouse() {
      if (!this.selectedWarehouse || !this.warehouses) return false
      const warehouse = this.warehouses.find(w => w.value === this.selectedWarehouse)
      return warehouse ? (warehouse.text || '').toLowerCase().includes('china') : false
    },

    isGermanyWarehouse() {
      if (!this.selectedWarehouse || !this.warehouses) return false
      const warehouse = this.warehouses.find(w => w.value === this.selectedWarehouse)
      return warehouse ? (warehouse.text || '').toLowerCase().includes('germany') : false
    }
  },

  watch: {
    warehouses: {
      handler(newWarehouses) {
        if (newWarehouses && newWarehouses.length > 0 && !this.selectedWarehouse) {
          const turkey = newWarehouses.find(w => (w.text || '').toLowerCase().includes('turkey'))
          const defaultWarehouse = turkey || newWarehouses[0]
          this.selectedWarehouse = defaultWarehouse.value
          this.handleSelectWarehouse(this.selectedWarehouse)
        }
      },
      immediate: true
    },

    checkAlert(val) {
      if (val) {
        let self = this
        setTimeout(() => {
          self.resetForm()
        }, 2200)
      }
    },

    kurdistanBankId: {
      handler(newVal) {
        if (newVal && !this.purchaseBankId) {
          this.purchaseBankId = newVal
        }
      },
      immediate: true
    }
  },

  methods: {
    filterCustomers(item, queryText, itemText) {
      const query = String(queryText || '').toLowerCase().trim()
      if (!query) return true

      const visibleFields = [
        itemText,
        item && item.full_name,
        item && item.phone_number,
        item && item.phone_number_2
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase())

      if (visibleFields.some(value => value.includes(query))) {
        return true
      }

      const normalizedQuery = this.normalizePhoneSearch(query)
      if (!normalizedQuery) {
        return false
      }

      return [item && item.phone_number, item && item.phone_number_2]
        .filter(Boolean)
        .some(value => this.normalizePhoneSearch(value).includes(normalizedQuery))
    },

    normalizePhoneSearch(value) {
      return String(value || '').replace(/\D/g, '')
    },

    getLoggedInUser() {
      this.loggedInUserData = JSON.parse(localStorage.getItem('user_data')) || {}
    },

    fetchLatestCurrencyRate() {
      const token = localStorage.getItem('access_token')
      this.$http
        .get('api/currency-rate/latest', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          if (response.data) {
            this.latestCurrencyRate = response.data
          }
        })
        .catch(() => {})
    },

    fetchBrandHistory() {
      const token = localStorage.getItem('access_token')
      axios.get('api/order-brand-history', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => { this.brandHistory = response.data.data || [] })
        .catch(() => { this.brandHistory = [] })
    },

    fetchSizeHistory() {
      const token = localStorage.getItem('access_token')
      axios.get('api/order-size-history', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => { this.sizeHistory = response.data.data || [] })
        .catch(() => { this.sizeHistory = [] })
    },

    rememberBrandAndSize() {
      this.rows.forEach(row => {
        if (row.brand && !this.brandHistory.some(b => b.toLowerCase() === row.brand.toLowerCase())) {
          this.brandHistory.unshift(row.brand)
        }
        if (row.size && !this.sizeHistory.some(s => s.toLowerCase() === row.size.toLowerCase())) {
          this.sizeHistory.unshift(row.size)
        }
      })
    },

    rowEstimatedUsdTotal(row) {
      const p = Number(row && row.price)
      const s = Number(row && row.shipping_cost)
      const q = Number(row && row.quantity)
      const price = Number.isFinite(p) ? p : 0
      const ship = Number.isFinite(s) ? s : 0
      const qty = Number.isFinite(q) && q > 0 ? q : 1

      // Mirror the backend warehouse-ID constants (Warehouse::TURKEY=1, USA=2, UAE=3, CHINA=4, UK=5, GERMANY=6)
      // item_price is in local currency; shipping is already USD. USA and UK are priced in USD (no conversion).
      let priceUsd = price
      const rate = this.latestCurrencyRate
      const wid = this.selectedWarehouse
      if (rate && wid && wid !== 2 && wid !== 5) {
        if (wid === 1 && rate.tl_rate > 0) priceUsd = price / rate.tl_rate
        else if (wid === 3 && rate.aed_rate > 0) priceUsd = price / rate.aed_rate
        else if (wid === 4 && rate.cny_rate > 0) priceUsd = price / rate.cny_rate
        else if (wid === 6 && rate.eur_rate > 0) priceUsd = price / rate.eur_rate
        else if (rate.ex_rate > 0) priceUsd = price / rate.ex_rate
      }

      return ((priceUsd + ship) * qty).toFixed(2)
    },

    handleSelectWarehouse(id) {
      this.selectedTransportationType = null
      const warehouse = this.warehouses.find(w => w.value === id)
      if (warehouse) {
        this.transportationTypes = warehouse.transportation_types || []
        if (this.transportationTypes.length > 0) {
          this.selectedTransportationType = this.transportationTypes[0].id
        }
      }
    },

    addRow() {
      if (this.rows.length >= MAX_ROWS) {
        this.showError(this.$t('orders.maxOrdersLimitError', { max: MAX_ROWS }))
        return
      }
      this.rows.push({
        id: ++this.nextRowId,
        product_name: '',
        brand: '',
        product_url: '',
        image: null,
        imagePreview: null,
        fileName: '',
        imageProcessing: false,
        imageToken: 0,
        /** Server filename once `image` has been uploaded, so a retry after a failed save doesn't re-upload it. */
        uploadedImage: null,
        quantity: 1,
        color: '',
        size: '',
        price: 0,
        shipping_cost: 0,
        note: ''
      })
    },

    addRows() {
      const requested = this.bulkRowCount && this.bulkRowCount > 0 ? this.bulkRowCount : 1
      const remaining = MAX_ROWS - this.rows.length
      if (remaining <= 0) {
        this.showError(this.$t('orders.maxOrdersLimitError', { max: MAX_ROWS }))
        return
      }
      const count = Math.min(requested, remaining)
      if (count < requested) {
        this.showError(this.$t('orders.onlyRowsAddedLimitError', { count, max: MAX_ROWS }))
      }
      for (let i = 0; i < count; i++) {
        this.addRow()
      }
    },

    resetRows() {
      this.discardRows(this.rows)
      this.rows = []
      this.addRow()
      this.bulkRowCount = null
    },

    /** Frees preview object URLs and releases any in-flight compression the rows still hold. */
    discardRows(rows) {
      rows.forEach(row => {
        if (row.imagePreview) URL.revokeObjectURL(row.imagePreview)
        if (row.imageProcessing) {
          row.imageProcessing = false
          this.pendingImageCount--
        }
      })
    },

    removeRow(id) {
      if (this.rows.length > 1) {
        const row = this.rows.find(r => r.id === id)
        if (row) this.discardRows([row])
        this.rows = this.rows.filter(r => r.id !== id)
      }
    },

    handleColorPick(event, row) {
      row.color = event.target.value
    },

    isValidHex(color) {
      return /^#[0-9A-Fa-f]{6}$/.test(color)
    },

    triggerRowFileInput(rowId) {
      const ref = this.$refs['fileInput_' + rowId]
      if (ref) {
        // $refs in v-for return array
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el) el.click()
      }
    },

    handleRowFileUpload(event, row) {
      const file = event.target.files[0]
      if (!file) return

      if (!isAllowedImageExtension(file.name)) {
        this.showError(this.$t('orders.unsupportedImageTypeError', { fileName: file.name, allowed: ALLOWED_IMAGE_EXTENSIONS.join(', ').toUpperCase() }))
        event.target.value = ''
        return
      }

      if (file.size > 50 * 1024 * 1024) {
        this.showError(this.$t('orders.fileSizeLimitError', { fileName: file.name }))
        event.target.value = ''
        return
      }

      if (/\.gif$/i.test(file.name) && file.size > MAX_GIF_BYTES) {
        this.showError(this.$t('orders.gifSizeLimitError', { fileName: file.name }))
        event.target.value = ''
        return
      }
      // Clear so picking the same file again after removing it still fires @change.
      event.target.value = ''

      // Compress now rather than at submit: previews then decode a ~300KB image instead of
      // up to 100 full-resolution photos, which could exhaust the tab's memory.
      const token = (row.imageToken || 0) + 1
      row.imageToken = token
      if (!row.imageProcessing) this.pendingImageCount++
      row.imageProcessing = true

      compressImageFile(file).then((compressed) => {
        // A newer pick for this row (or a reset/removal) superseded this one.
        if (row.imageToken !== token || !this.rows.includes(row)) return
        if (row.imagePreview) {
          URL.revokeObjectURL(row.imagePreview)
        }
        row.image = compressed
        row.uploadedImage = null
        row.fileName = file.name
        row.imagePreview = URL.createObjectURL(compressed)
      }).then(() => {
        if (row.imageToken === token && row.imageProcessing) {
          row.imageProcessing = false
          this.pendingImageCount--
        }
      })
    },

    openImagePreview(src) {
      this.previewDialogSrc = src
      this.imagePreviewDialog = true
    },

    showError(msg) {
      this.hasError = true
      this.errorMessage = msg
      setTimeout(() => {
        this.hasError = false
        this.errorMessage = ''
      }, 4000)
    },

    validate() {
      this.customerError = this.loggedInUserData.is_staff && !this.selectedCustomer
      this.warehouseError = !this.selectedWarehouse
      // transportation is auto-selected, no user error needed
      this.transportationError = false

      if (this.customerError || this.warehouseError) {
        return false
      }

      if (this.pendingImageCount > 0) {
        this.showError(this.$t('orders.imagesStillProcessingError'))
        return false
      }

      for (let i = 0; i < this.rows.length; i++) {
        const row = this.rows[i]
        const rowNum = i + 1

        if (!row.product_name || row.product_name.trim() === '') {
          this.showError(this.$t('orders.rowItemNameRequiredError', { row: rowNum }))
          return false
        }
        if (!row.product_url || row.product_url.trim() === '') {
          this.showError(this.$t('orders.rowLinkRequiredError', { row: rowNum }))
          return false
        }
        if (!row.quantity || row.quantity < 1) {
          this.showError(this.$t('orders.rowQuantityMinError', { row: rowNum }))
          return false
        }
      }
      return true
    },

    submitAllOrders() {
      if (!this.validate()) return

      // Map price to the correct field based on warehouse type
      this.rows.forEach(row => {
        if (!row.brand || !row.brand.trim()) {
          const words = (row.product_name || '').trim().split(' ')
          row.brand = [words[0] || '', words[1] || ''].join(' ').trim()
        }
        // Default to 0 if the field was cleared to blank rather than left at its
        // initial 0 — v-model.number leaves an emptied number input as '', not 0.
        const shipping = Number(row.shipping_cost)
        row.shipping_cost = Number.isFinite(shipping) ? shipping : 0
        if (this.isTurkeyWarehouse) {
          row.the_tl_price = row.price
          row.item_price = row.price
          row.the_aed_price = 0
          row.the_cny_price = 0
          row.the_gbp_price = 0
          row.the_eur_price = 0
        } else if (this.isUAEWarehouse) {
          row.the_aed_price = row.price
          row.item_price = row.price
          row.the_tl_price = 0
          row.the_cny_price = 0
          row.the_gbp_price = 0
          row.the_eur_price = 0
        } else if (this.isChinaWarehouse) {
          row.the_cny_price = row.price
          row.item_price = row.price
          row.the_tl_price = 0
          row.the_aed_price = 0
          row.the_gbp_price = 0
          row.the_eur_price = 0
        } else if (this.isGermanyWarehouse) {
          row.the_eur_price = row.price
          row.item_price = row.price
          row.the_tl_price = 0
          row.the_aed_price = 0
          row.the_cny_price = 0
          row.the_gbp_price = 0
        } else {
          row.item_price = row.price
          row.the_tl_price = 0
          row.the_aed_price = 0
          row.the_cny_price = 0
          row.the_gbp_price = 0
          row.the_eur_price = 0
        }
      })

      this.rememberBrandAndSize()

      var self = this
      self.isSubmitting = true
      self.makeMultipleOrders(
        self.rows,
        self.selectedCustomer,
        self.selectedWarehouse,
        self.selectedTransportationType,
        self.sendToPurchase,
        self.purchaseBankId,
        function (progress) { self.uploadProgress = progress }
      ).then(function () {
        self.isSubmitting = false
        self.uploadProgress = null
        self.handleClose()
      }).catch(function () {
        self.isSubmitting = false
        self.uploadProgress = null
      })
    },

    resetForm() {
      this.discardRows(this.rows)
      this.rows = []
      this.uploadProgress = null
      this.addRow()
      this.selectedCustomer = null
      this.sendToPurchase = false
      this.purchaseBankId = this.kurdistanBankId
      // Re-auto-select Turkey warehouse after reset
      if (this.warehouses && this.warehouses.length > 0) {
        const turkey = this.warehouses.find(w => (w.text || '').toLowerCase().includes('turkey'))
        const defaultWarehouse = turkey || this.warehouses[0]
        this.selectedWarehouse = defaultWarehouse.value
        this.handleSelectWarehouse(this.selectedWarehouse)
      } else {
        this.selectedWarehouse = null
        this.selectedTransportationType = null
        this.transportationTypes = []
      }
      this.hasError = false
      this.errorMessage = ''
      this.customerError = false
      this.warehouseError = false
      this.transportationError = false
      this.bulkRowCount = null
    },

    handleClose() {
      this.resetForm()
      this.closeAddMultipleOrdersModal()
    }
  }
}
</script>

<style scoped>
.table th {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #333;
  font-size: 12px;
}

.table td,
.table th {
  padding: 6px 4px !important;
  vertical-align: middle !important;
}

.table .form-control {
  height: 32px;
  padding: 4px 8px;
  font-size: 13px;
}

.bulk-action-btn.v-btn {
  height: auto !important;
  min-height: 36px !important;
  margin: 0 !important;
}

.bulk-count-input {
  width: 80px;
  height: 36px;
  padding: 4px 8px;
  font-size: 13px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  box-sizing: border-box;
  vertical-align: middle;
}

.bulk-send-to-purchase-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  white-space: nowrap;
  cursor: pointer;
  font-size: 15px;
  margin: 0;
  color: #333;
}

.bulk-send-to-purchase-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.bulk-bank-select {
  width: 200px;
  font-size: 15px;
}
</style>



// WEBPACK FOOTER //
// src/components/addMultipleOrders.vue